import React, { useState, useRef, useEffect } from 'react';
import { getLanguages } from '../api/languageApi';
import { useTranslation } from '../context/LanguageContext';
import './LanguageSwitcher.css';

const GlobeIcon = () => (
  <svg className="lang-switcher-icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

// Navbar language dropdown: lists the company's languages and applies the
// chosen one immediately (fetches + stores its labels).
export default function LanguageSwitcher() {
  const { language, changeLanguage, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingCode, setSavingCode] = useState(null);
  const [error, setError] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    setError('');
    // The list is kept after the first load so reopening doesn't flash a loader.
    if (!next || languages.length) return;
    setLoading(true);
    getLanguages()
      .then(setLanguages)
      .catch(() => setError(t('agency.lang_load_failed_msg', 'Could not load languages. Please try again.')))
      .finally(() => setLoading(false));
  };

  const handleSelect = async (lang) => {
    if (savingCode) return;
    if (lang.code === language?.code) { setOpen(false); return; }
    setSavingCode(lang.code);
    setError('');
    try {
      await changeLanguage(lang);
      setOpen(false);
    } catch {
      setError(t('agency.lang_switch_failed_error', 'Could not load translations. Please try again.'));
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <div className={`lang-switcher${open ? ' is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="lang-switcher-btn"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <GlobeIcon />
        <span>{language?.nativeName || language?.name || t('agency.lang_switcher_label', 'Language')}</span>
        <svg className="lang-switcher-caret" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <div className="lang-switcher-menu" role="listbox" aria-label={t('agency.lang_switcher_list_alt', 'Select language')}>
        {loading && <div className="lang-switcher-status"><span className="lang-switcher-spinner" /></div>}
        {!loading && !error && languages.length === 0 && (
          <div className="lang-switcher-status">{t('agency.lang_none_msg', 'No languages available')}</div>
        )}
        {!loading && languages.map((lang) => {
          const selected = lang.code === language?.code;
          return (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={selected}
              className={`lang-switcher-option${selected ? ' is-selected' : ''}`}
              disabled={!!savingCode && savingCode !== lang.code}
              onClick={() => handleSelect(lang)}
            >
              <span className="lang-switcher-native">{lang.nativeName || lang.name}</span>
              {lang.nativeName && lang.nativeName !== lang.name && (
                <span className="lang-switcher-name">{lang.name}</span>
              )}
              {savingCode === lang.code
                ? <span className="lang-switcher-spinner" />
                : selected && (
                  <svg className="lang-switcher-check" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
                    <path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
            </button>
          );
        })}
        {error && <div className="lang-switcher-error">{error}</div>}
      </div>
    </div>
  );
}
